import { Formik, Form } from 'formik';

import { FormActions } from '@@/form-components/FormActions';

import { DockerRBACFormData } from './types';
import { DockerRBACInnerForm } from './DockerRBACInnerForm';

interface Props {
  initialValues: DockerRBACFormData;
  onSubmit: (values: DockerRBACFormData) => void | Promise<void>;
  /** Mode: 'create' | 'edit' */
  mode: 'create' | 'edit';
  /** Whether the policy name has been provided (for validation) */
  nameProvided: boolean;
  /** Whether the environment groups have been provided (for validation) */
  environmentGroupsProvided: boolean;
}

/**
 * Docker RBAC Policy Form Component
 *
 * Renders the access management form for Docker/Swarm/Podman RBAC policies.
 * Can be used for both creating new policies and editing existing ones.
 */
export function DockerRBACPolicyForm({
  initialValues,
  onSubmit,
  mode,
  nameProvided,
  environmentGroupsProvided,
}: Props) {
  return (
    <Formik<DockerRBACFormData>
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnMount
    >
      {({ values, setFieldValue, isValid, isSubmitting }) => (
        <Form>
          <DockerRBACInnerForm
            formData={values}
            onChange={(key, value) => setFieldValue(key, value)}
            isCreating={mode === 'create'}
          />

          <FormActions
            submitLabel={mode === 'create' ? 'Create policy' : 'Update policy'}
            loadingText={mode === 'create' ? 'Creating...' : 'Updating...'}
            isLoading={isSubmitting}
            isValid={
              isValid &&
              !!nameProvided &&
              !!environmentGroupsProvided &&
              (values.accessList?.length ?? 0) > 0
            }
            data-cy="policy-form-actions"
          />
        </Form>
      )}
    </Formik>
  );
}
